import { AppHeader } from "~/components/AppHeader"
import { TooltipButton } from "~/components/ui/button"
import { Item } from "~/components/ui/item"
import { Input } from "~/components/ui/input"

import {
	ExternalLinkIcon,
	LinkIcon,
	MessageCircleIcon,
	PlusIcon,
	RefreshCcwIcon,
	ThumbsUpIcon,
	UnlinkIcon
} from "lucide-react"
import { useEffect, useRef, useState } from "react"
import { useParams } from "react-router-dom"
import { Link } from "~/components/Link"
import { toast } from "sonner"
import {
	ActivityCommentCard,
	CommentComposer
} from "~/components/activity/Comments"
import { ActivityExternalLink } from "~/components/ActivityExternalLink"
import { DetailsRow, DetailsSection } from "~/components/details/DetailsSection"
import { DetailsLayout } from "~/components/details/DetailsLayout"
import { Card } from "~/components/ui/card"
import { StatusIcon } from "~/components/icons/status"
import { IssueSetting } from "~/components/issueDetailsPage/IssueSetting"
import {
	captureFeatureFlagInteraction,
	captureFeatureFlagView
} from "~/components/providers/Posthog"
import { LoadingState } from "~/components/screens/StateScreen"
import { UnsavedChanges } from "~/components/UnsavedChanges"
import { EntityIdentity } from "~/components/EntityIdentity"
import { Button } from "~/components/ui/button"
import { Search } from "~/components/ui/search"
import { useSidebar } from "~/components/ui/sidebar"
import { Textarea } from "~/components/ui/textarea"
import { TooltipProvider } from "~/components/ui/tooltip"
import { usePermission } from "~/hooks/usePermission"
import { useTeam } from "~/hooks/useTeam"
import type { IssueStatus } from "~/lib/enums"
import { TeamPermissions } from "~/lib/permissions"
import { enumFormat, shortRelativeTime, textToEditorContent } from "~/lib/utils"
import { api } from "~/trpc/react"

export default function TeamSuggestionDetailsPage() {
	const { team, isLoading: isTeamLoading } = useTeam()
	const { suggestionId = "" } = useParams<{ suggestionId: string }>()
	const utils = api.useUtils()
	const appSidebar = useSidebar()
	const { hasTeamPermission } = usePermission({ activeTeamId: team?.id })
	const [issueQuery, setIssueQuery] = useState("")
	const [commentText, setCommentText] = useState(textToEditorContent(null))
	const loadedSuggestionId = useRef<string | null>(null)
	const [suggestionState, setSuggestionState] = useState<{
		title: string
		description: string
		status: IssueStatus
	} | null>(null)

	const { data: suggestion, isLoading } = api.suggestions.get.useQuery(
		{ teamId: team?.id ?? "", suggestionId },
		{ enabled: !!team?.id && !!suggestionId }
	)
	const { data: issues } = api.suggestions.searchIssues.useQuery(
		{ teamId: team?.id ?? "", query: issueQuery },
		{ enabled: !!team?.id && !suggestion?.linkedIssue }
	)

	const canEdit = hasTeamPermission(TeamPermissions.EDIT_ISSUES)
	const canCreateIssue = hasTeamPermission(TeamPermissions.CREATE_ISSUES)
	const canComment = hasTeamPermission(TeamPermissions.COMMENT_ISSUES)

	const invalidateSuggestion = () => {
		void utils.suggestions.get.invalidate({
			teamId: team?.id ?? "",
			suggestionId
		})
		void utils.suggestions.list.invalidate({ teamId: team?.id ?? "" })
		void utils.user.getTeams.invalidate()
	}

	const edit = api.suggestions.edit.useMutation({
		onSuccess: (updated) => {
			captureFeatureFlagInteraction("suggestions")
			if (updated) {
				setSuggestionState({
					title: updated.title,
					description: updated.description ?? "",
					status: updated.status
				})
			}
			invalidateSuggestion()
			toast.success("Suggestion saved")
		},
		onError: (error) => toast.error(error.message)
	})
	const comment = api.suggestions.comment.useMutation({
		onSuccess: () => {
			captureFeatureFlagInteraction("suggestions")
			setCommentText(textToEditorContent(null))
			invalidateSuggestion()
			toast.success("Comment posted")
		},
		onError: (error) => toast.error(error.message)
	})
	const linkIssue = api.suggestions.linkIssue.useMutation({
		onSuccess: () => {
			captureFeatureFlagInteraction("suggestions")
			invalidateSuggestion()
			toast.success("Issue link updated")
		},
		onError: (error) => toast.error(error.message)
	})
	const createIssue = api.suggestions.createIssue.useMutation({
		onSuccess: () => {
			captureFeatureFlagInteraction("suggestions")
			invalidateSuggestion()
			void utils.issues.getTeamIssues.invalidate()
			toast.success("Issue created")
		},
		onError: (error) => toast.error(error.message)
	})
	const syncToDiscord = api.suggestions.syncToDiscord.useMutation({
		onSuccess: () => {
			captureFeatureFlagInteraction("suggestions")
			toast.success("Discord sync queued")
		},
		onError: (error) => toast.error(error.message)
	})

	useEffect(() => {
		if (!suggestion || loadedSuggestionId.current === suggestion.id) return
		loadedSuggestionId.current = suggestion.id
		setSuggestionState({
			title: suggestion.title,
			description: suggestion.description ?? "",
			status: suggestion.status
		})
		captureFeatureFlagView("suggestions")
	}, [suggestion])

	api.suggestions.onUpdate.useSubscription(
		{ teamId: team?.id ?? "" },
		{
			enabled: !!team?.id,
			onData: (update) => {
				if (!update.suggestionId || update.suggestionId === suggestionId) {
					invalidateSuggestion()
				}
			}
		}
	)

	if (isTeamLoading || isLoading) return <LoadingState />
	if (!suggestion || !suggestionState || !team) return null

	const hasChanged =
		suggestionState.title !== suggestion.title ||
		suggestionState.description !== (suggestion.description ?? "") ||
		(!suggestion.linkedIssueId && suggestionState.status !== suggestion.status)
	const displayStatus = suggestion.linkedIssue?.status ?? suggestionState.status
	const publicPath = `/s/${team.organization.slug}/${team.slug}/${suggestion.friendlyId}`

	const detailsContent = (
		<div className="flex min-w-0 flex-col gap-4">
			<Card variant="outline" density="flush" className="min-w-0">
				<DetailsSection title="Workflow" variant="grouped">
					<div className="flex min-w-0 flex-col gap-3">
						<DetailsRow label="Status">
							<IssueSetting<IssueStatus>
								disabled={
									!canEdit || edit.isPending || !!suggestion.linkedIssueId
								}
								ariaLabel="Suggestion status"
								value={displayStatus}
								onChange={(status) =>
									setSuggestionState({ ...suggestionState, status })
								}
								options="status"
								showArrow
								showBorder
							/>
						</DetailsRow>
						{suggestion.linkedIssueId && (
							<p className="text-xs text-text-small">
								Status is synced from the linked issue.
							</p>
						)}
					</div>
				</DetailsSection>

				<DetailsSection title="Signals" variant="grouped">
					<div className="flex flex-col gap-3 text-sm">
						<DetailsRow label="Votes">
							<span className="flex items-center gap-1 font-medium text-text-main">
								<ThumbsUpIcon className="size-4 text-icons" />
								{suggestion.voteCount}
							</span>
						</DetailsRow>
						<DetailsRow label="Discord votes">
							<span className="font-medium text-text-main">
								{suggestion.discordVoteCount}
							</span>
						</DetailsRow>
						<DetailsRow label="Comments">
							<span className="flex items-center gap-1 font-medium text-text-main">
								<MessageCircleIcon className="size-4 text-icons" />
								{suggestion.comments.length}
							</span>
						</DetailsRow>
						<DetailsRow label="Source">
							<span className="font-medium text-text-main">
								{suggestion.source}
							</span>
						</DetailsRow>
					</div>
				</DetailsSection>
			</Card>
			<Card variant="outline" density="flush" className="min-w-0">
				<DetailsSection title="Issue link" variant="grouped">
					{suggestion.linkedIssue ? (
						<div className="flex min-w-0 flex-col gap-3">
							<Link
								to={`/${team.organization.slug}/${team.slug}/${suggestion.linkedIssue.friendlyId}`}
								className="flex items-center justify-between gap-3 rounded-md px-2 py-1.5 text-sm hover:bg-active/70"
							>
								<span className="min-w-0 truncate font-medium text-text-main">
									#{suggestion.linkedIssue.friendlyId}
								</span>
								<span className="flex items-center gap-1 text-text-small">
									<StatusIcon status={suggestion.linkedIssue.status} />
									{enumFormat(suggestion.linkedIssue.status)}
								</span>
							</Link>
							<Button
								variant="ghost"
								className="justify-start"
								disabled={!canEdit || linkIssue.isPending}
								onClick={() =>
									linkIssue.mutate({
										teamId: team.id,
										suggestionId: suggestion.id,
										issueId: null
									})
								}
							>
								<UnlinkIcon className="size-4" />
								Unlink issue
							</Button>
						</div>
					) : (
						<div className="flex min-w-0 flex-col gap-3">
							<Search
								mode="input"
								placeholder="Search issues"
								value={issueQuery}
								onChange={setIssueQuery}
							/>
							<div className="max-h-64 min-w-0 overflow-x-hidden overflow-y-auto">
								{issues?.map((issue) => (
									<Item
										asChild
										variant="interactive"
										size="compact"
										key={issue.id}
									>
										<button
											type="button"
											disabled={!canEdit || linkIssue.isPending}
											onClick={() =>
												linkIssue.mutate({
													teamId: team.id,
													suggestionId: suggestion.id,
													issueId: issue.id
												})
											}
											className="min-w-0 w-full text-left"
										>
											<span className="min-w-0 flex-1 truncate text-text-main">
												#{issue.friendlyId} · {issue.title}
											</span>
											<StatusIcon status={issue.status} />
										</button>
									</Item>
								))}
								{issues?.length === 0 && (
									<p className="px-2 py-1.5 text-sm text-text-small">
										No issues found.
									</p>
								)}
							</div>
							<Button
								variant="ghost"
								className="justify-start"
								disabled={!canCreateIssue || createIssue.isPending}
								onClick={() =>
									createIssue.mutate({
										teamId: team.id,
										suggestionId: suggestion.id
									})
								}
								pending={createIssue.isPending}
								icon={<PlusIcon className="size-4" />}
							>
								Create issue from suggestion
							</Button>
						</div>
					)}
				</DetailsSection>

				<DetailsSection title="Links" variant="grouped">
					<div className="flex flex-col gap-2">
						<Button
							asChild
							variant="outline"
							className="justify-start text-text-main"
						>
							<Link to={publicPath} target="_blank" rel="noreferrer">
								<ExternalLinkIcon className="size-4" />
								Open public page
							</Link>
						</Button>
						{suggestion.discordThreadId && suggestion.discordChannelId && (
							<Button
								asChild
								variant="outline"
								className="justify-start text-text-main"
							>
								<ActivityExternalLink
									href={`https://discord.com/channels/${suggestion.discordGuildId}/${suggestion.discordThreadId}`}
								>
									<LinkIcon className="size-4" />
									Open Discord thread
								</ActivityExternalLink>
							</Button>
						)}
					</div>
				</DetailsSection>
			</Card>
		</div>
	)

	return (
		<main className="flex h-full min-h-0 min-w-0 flex-col overflow-hidden">
			<DetailsLayout
				title="Suggestion details"
				details={detailsContent}
				header={(detailsToggle) => (
					<TooltipProvider>
						<AppHeader
							sidebarToggle={appSidebar.toggleSidebar}
							actions={
								<>
									<TooltipButton
										asChild
										variant="outline"
										size="icon-md"
										tooltip="Open public suggestion"
									>
										<Link to={publicPath} target="_blank" rel="noreferrer">
											<ExternalLinkIcon className="size-4" />
										</Link>
									</TooltipButton>
									<TooltipButton
										variant="outline"
										size="icon-md"
										disabled={syncToDiscord.isPending || !canEdit}
										onClick={() =>
											syncToDiscord.mutate({
												teamId: team.id,
												suggestionId: suggestion.id
											})
										}
										tooltip="Sync Discord"
									>
										<RefreshCcwIcon className="size-4" />
									</TooltipButton>
									{detailsToggle}
								</>
							}
						>
							<div className="flex min-w-0 grow items-center gap-1.5 font-medium">
								<Link
									className="min-w-0 truncate"
									to={`/${team.organization.slug}/${team.slug}/suggestions`}
								>
									<EntityIdentity
										kind="team"
										name={team.name}
										image={team.logo}
									/>
								</Link>
								<span className="text-text-small">/</span>
								<span className="text-text-main">Suggestions</span>
								<span className="text-text-small">/</span>
								<span className="min-w-0 truncate text-text-main">
									#{suggestion.friendlyId}
								</span>
							</div>
						</AppHeader>
					</TooltipProvider>
				)}
			>
				<div className="min-h-0 min-w-0 flex-1 overflow-x-hidden overflow-y-auto px-4 py-6 sm:px-6">
					<div className="mx-auto flex w-full max-w-5xl flex-col gap-7 pb-16">
						<section aria-label="Suggestion content" className="min-w-0">
							<Input
								placeholder=" Suggestion title..."
								value={suggestionState.title}
								onChange={(event) =>
									setSuggestionState({
										...suggestionState,
										title: event.target.value
									})
								}
								disabled={!canEdit}
								className="mb-2"
								variant="title"
							/>
							<Textarea
								placeholder="Suggestion description..."
								value={suggestionState.description}
								onChange={(event) =>
									setSuggestionState({
										...suggestionState,
										description: event.target.value
									})
								}
								disabled={!canEdit}
								variant="document"
							/>
						</section>
						<section className="min-w-0 max-w-full border-t border-stroke pt-6 pb-4">
							<h3 className="mb-4 font-medium text-text-main">Activity</h3>
							<CommentComposer
								value={commentText}
								onChange={setCommentText}
								onSubmit={async () => {
									await comment.mutateAsync({
										teamId: team.id,
										suggestionId: suggestion.id,
										content: commentText
									})
								}}
								placeholder={
									canComment
										? "Write a comment..."
										: "You do not have permission to comment"
								}
								disabled={!canComment}
								isPending={comment.isPending}
								submitLabel="Post comment"
								className="mb-5"
							/>
							{suggestion.comments.length ? (
								<div className="flex min-w-0 max-w-full flex-col gap-3">
									{suggestion.comments.map((item) => (
										<ActivityCommentCard
											key={item.id}
											id={`comment-${item.id}`}
											content={item.content}
											authorName={item.authorName ?? "Unknown"}
											authorImage={item.authorImage}
											createdAt={item.createdAt}
											formatRelativeTimestamp={(date) =>
												`${shortRelativeTime(date)} ago`
											}
											badges={
												item.source === "Discord" ? (
													<span className="text-text-small text-xs leading-none">
														from Discord
													</span>
												) : null
											}
										/>
									))}
								</div>
							) : (
								<p className="text-text-small">No activity yet.</p>
							)}
						</section>
					</div>
				</div>
				<UnsavedChanges
					isVisible={canEdit && hasChanged}
					canSave={!!suggestionState.title.trim()}
					onSave={async () => {
						await edit.mutateAsync({
							teamId: team.id,
							suggestionId: suggestion.id,
							title: suggestionState.title,
							description: suggestionState.description || null,
							status: suggestionState.status
						})
					}}
					onReset={async () => {
						setSuggestionState({
							title: suggestion.title,
							description: suggestion.description ?? "",
							status: suggestion.status
						})
					}}
				/>
			</DetailsLayout>
		</main>
	)
}
